import "antd/dist/reset.css";

import "./parse.config";

import { init, produce, StorageAdapterLS } from "@opendash/core";
import { getCurrentLanguageSync, resolveTranslation } from "@opendash/i18n";
import { registerIconPack } from "@opendash/icons";
import { HighchartsPlugin } from "@opendash/plugin-highcharts";
import { $monitoring, MonitoringPlugin } from "@opendash/plugin-monitoring";
import { OpenwarePlugin } from "@opendash/plugin-openware";
import { $parse, ParsePlugin } from "@opendash/plugin-parse";
import { ParseMonitoringPlugin } from "@opendash/plugin-parse-monitoring";
import { TimeseriesPlugin } from "@opendash/plugin-timeseries";

import { Parse } from "parse-hooks";

init("opendash", async (factory) => {
  // @ts-ignore
  registerIconPack(await import("@opendash/icons/dist/fa-regular.json"));
  factory.ui.setLogoImage("/si.jpeg");

  factory.registerLanguage("en", "English");
  factory.registerLanguage("de", "Deutsch", "en", true);

  factory.registerTranslationResolver(
    "de",
    "parse-custom",
    async () => (await import("./translations/de.json")).default,
  );

  factory.registerTranslationResolver(
    "en",
    "parse-custom",
    async () => (await import("./translations/en.json")).default,
  );

  const currentLang = getCurrentLanguageSync();
  await Promise.all([resolveTranslation("parse-custom", currentLang)]);

  factory.ui.setHeaderSourcePickerProps({
    additionalInfoInSourcePicker: true,
    additionalInfoField: "tag",
    additionalInfoFallbackField: "tag",
    additionalInfoFieldFilter: "tenant-",
  });

  factory.registerAntDesignTranslation(
    "en",
    () => import("antd/lib/locale/en_US"),
  );

  factory.registerAntDesignTranslation(
    "de",
    () => import("antd/lib/locale/de_DE"),
  );

  // Adapter + Plugins
  factory.registerDeviceStorageAdapter(new StorageAdapterLS());

  await factory.use(new TimeseriesPlugin());
  await factory.use(
    new MonitoringPlugin({
      hideMonitoringOnFrontPage: true,
      autoCreateInitialDashboard: true,
      autoCreateWidgetOptions: {
        autoCreateWidgets: true,
        autoCreateWidgetsForSource: false,
        autoCreateWidgetsEveryTime: false,
        autoCreateWidgetsEveryTimeForSource: false,
        autoCreateWidgetsContent: "dimension",
        autoCreateWidgetsContentForSource: "dimension",
        autoCreateWidgetsType: "opendash-widget-hc-kpi",
        autoCreateWidgetsForSourceType: "opendash-widget-hc-kpi",
        limitAutoCreateWidgets: 100,
      },
      widgetSorting: {
        enabled: false,
        valueTypeNames: [],
        autoSortOnFirstRender: {
          enabled: true,
          sourceTagIncludes: ["tenant-"],
        },
      },
      disableWidgets: {
        TimeControlWidget: true,
        NavigationWidget: false,
      },
    }),
  );

  await factory.use(
    new ParsePlugin({
      createAccount: false,
      authLdapActive: false,
      useLiveQueries: false,
      disableNavigationWidgetPreset: true,
      disableNavigationNotificationSettings: true,
      enableAlarmManagementAndGroups: true,
    }),
  );
  await factory.use(
    new ParseMonitoringPlugin({
      liveQueries: false,
    }),
  );

  await factory.use(
    new OpenwarePlugin({
      secure: true,
      host: "siautomation.openinc.de",
      //filterValuesOlderThanMS: 15742800000,
      hideAlarmOnFrontPage: true,
      hideReportingOnFrontPage: true,
      disableFeature: {
        menu: {
          DataMeta: true,
          DataHierachies: true,
        },
        widgets: {
          owActuators: true,
          sankey: false,
          sunburst: true,
          tableAggregateWidget: true,
        },
        slideshow: true,
        dataCollection: false,
        VKPI: true,
        reporting: false,
        reportingFeatures: {
          adhoc: false,
          schedule: false,
          img: true,
          export: false,
        },
      },
    }),
  );

  await factory.use(
    new HighchartsPlugin({
      disableWidgets: {
        Dependency: true,
        TimeSeriesCompareWidget: true,
        XYCompare: true,
        TableWidget: false,
        TableEnergyStep: false,
        KPIMappedWidget: false,
        HCPivotOI: true,
        PivotSunburst: true,
        KPIWidget: false,
        KPICanvasWidget: false,
        MultiKPIWidget: false,
        KPIMinMaxWidget: false,
        PatternWidget: false,
        PatternHeatmapWidget: false,
        PieWidget: false,
        StatesWidget: false,
        StatesGanttWidget: false,
        TimeSeriesWidget: false,
        PivotTimeseries: false,
      },
    }),
  );

  factory.registerStaticNavigationItem({
    id: "monitoring/dashboard",
    group: "monitoring",
    place: "frontpage",
    order: 1,
    label: "opendash:monitoring.label",
    icon: "fa:chart-line",
    color: "#B51413",
    link: "/monitoring/dashboards",
    routeCondition: "**",
    activeCondition: "/",
  });

  /*
  factory.registerStaticNavigationItem({
    id: "openware/reporting",
    group: "openware",
    place: "frontpage",
    order: 2,
    event: "string",
    color: "#B51413",
    label: "openware:reporting.label",
    icon: "fa:file",
    link: "/openware/reporting/adhoc",
    routeCondition: "**",
    activeCondition: "/",
    permission: "monitoring:reporting-can-see",
  });
  
  factory.registerStaticNavigationItem({
    id: "mailgroups",
    group: "mailgroups",
    place: "frontpage",
    order: 3,
    label: "opendash:mailgroups.label",
    icon: "fa:bell",
    color: "#B51413",
    link: "/alarm_management/list",
    routeCondition: "**",
    activeCondition: "/",
    permission: "opendash:can-access-mailgroups",
  });
  */

  $parse.ui.setClassConfig({
    className: "OD3_WidgetPreset",
    titleFields: ["label"],
    displayFields: ["label", "tags", "description"],
    createFields: [],
    editFields: ["label", "tags", "description"],
  });

  $parse.ui.setClassConfig({
    className: "_User",
    titleFields: ["name"],
    displayFields: [
      "name",
      "tenant",
      "tenantAdmin",
      "tenantVerified",
      "email",
      "updatedAt",
    ],
    createFields: [
      "name",
      "email",
      "tenant",
      "tenantAdmin",
      "tenantVerified",
      "password",
    ],
    editFields: [
      "name",
      "email",
      "tenant",
      "tenantAdmin",
      "tenantVerified",
      "password",
    ],
    beforeSave: (object) => {
      if (object.get("email")) {
        object.set("username", object.get("email"));
      }
    },
  });

  $parse.ui.setDefaultView("OD3_WidgetPreset", {
    type: "table",
  });

  $parse.ui.setClassConfig({
    className: "OD3_Tenant",
    titleFields: ["label"],
    displayFields: ["label"],
    createFields: ["label"],
    editFields: ["label"],
  });

  $parse.ui.setClassConfig({
    className: "OD3_Permission",
    titleFields: ["label"],
    displayFields: ["label", "key"],
    createFields: ["label", "key"],
    editFields: ["label", "key"],
  });

  $parse.ui.setClassConfig({
    className: "OD3_Dashboard",
    titleFields: ["name"],
    displayFields: ["name", "source"],
    createFields: ["name"],
    editFields: ["name"],
  });

  $parse.ui.setDefaultView("OD3_Source", {
    type: "table",
    showSearch: true,
  });

  $parse.ui.setDefaultView("OD3_Tenant", {
    type: "table",
    showSearch: true,
  });

  $parse.ui.setClassConfig({
    className: "OD3_Alarm",
    titleFields: ["user"],
    displayFields: [
      "user",
      "condition",
      "trigger",
      "action",
      "createdAt",
      "updatedAt",
    ],
    createFields: ["user", "condition", "trigger", "action"],
    editFields: ["user", "condition", "trigger", "action"],
  });

  $parse.ui.setDefaultView("OD3_Alarm", {
    type: "table",
  });

  $parse.ui.setClassConfig({
    className: "_Role",
    titleFields: ["label"],
    displayFields: ["label", "name", "users", "createdAt", "updatedAt"],
    createFields: ["label", "name"],
    editFields: ["label", "name", "users"],
    defaultACL: () => {
      if (!$parse.user.id()) {
        return {};
      }

      const userId = $parse.user.id() || "";
      return {
        [userId]: {
          read: true,
          write: true,
        },
      };
    },
  });
  $parse.ui.setDefaultView("_Role", {
    type: "table",
    showSearch: true,
  });

  $parse.ui.setDefaultView("_User", {
    type: "table",
    showSearch: true,
  });
}).then((app) => {
  console.log("init open.DASH");
});
